import { FormattedMessage, useIntl } from '@umijs/max';
import { Button, Typography } from 'antd';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { BillingRequiredEventDetail } from '../../constants/billing';
import { BILLING_REQUIRED_EVENT } from '../../constants/billing';

interface BillingRequiredOverlayProps {
  children: React.ReactNode;
  canManageBilling: boolean;
  hidden?: boolean;
  onManageBilling: () => void;
}

const BillingRequiredOverlay: React.FC<BillingRequiredOverlayProps> = ({
  children,
  canManageBilling,
  hidden = false,
  onManageBilling,
}) => {
  const intl = useIntl();
  const defaultMessage = intl.formatMessage({
    id: 'components.billingRequiredOverlay.defaultMessage',
    defaultMessage:
      'Your organisation billing requires attention before this action can continue. Please contact your organisation admin.',
  });
  const [visible, setVisible] = useState(false);
  const [message, setMessage] = useState(defaultMessage);

  useEffect(() => {
    if (hidden) {
      setVisible(false);
      return undefined;
    }

    const handleBillingRequired = (event: Event) => {
      const detail = (event as CustomEvent<BillingRequiredEventDetail>).detail;
      if (detail?.message) setMessage(detail.message);
      setVisible(true);
    };

    window.addEventListener(BILLING_REQUIRED_EVENT, handleBillingRequired);
    return () => window.removeEventListener(BILLING_REQUIRED_EVENT, handleBillingRequired);
  }, [hidden]);

  const handleManageBilling = () => {
    setVisible(false);
    onManageBilling();
  };

  const overlay = visible && !hidden && (
    <dialog
      open
      data-testid="billing-required-overlay"
      aria-modal="true"
      aria-labelledby="billing-required-title"
      style={{
        boxSizing: 'border-box',
        width: '100vw',
        height: '100vh',
        maxWidth: 'none',
        maxHeight: 'none',
        margin: 0,
        border: 0,
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        background: 'rgba(48, 50, 54, 0.72)',
        backdropFilter: 'blur(2px)',
      }}
    >
      <div
        style={{
          width: 'min(100%, 460px)',
          padding: '32px 36px',
          border: '1px solid rgba(255, 255, 255, 0.55)',
          borderTop: '4px solid #72757a',
          borderRadius: 12,
          background: '#f5f4f1',
          boxShadow: '0 18px 48px rgba(0, 0, 0, 0.24)',
          textAlign: 'center',
        }}
      >
        <Typography.Title
          id="billing-required-title"
          level={3}
          style={{ marginTop: 0, marginBottom: 12, color: '#3d4146' }}
        >
          <FormattedMessage
            id="components.billingRequiredOverlay.title"
            defaultMessage="Billing required"
          />
        </Typography.Title>
        <Typography.Paragraph style={{ marginBottom: canManageBilling ? 24 : 0 }}>
          {message}
        </Typography.Paragraph>
        {canManageBilling && (
          <Button type="primary" onClick={handleManageBilling}>
            <FormattedMessage
              id="components.billingRequiredOverlay.manageBillingButton"
              defaultMessage="Manage billing"
            />
          </Button>
        )}
      </div>
    </dialog>
  );

  return (
    <>
      {children}
      {overlay && typeof document !== 'undefined' ? createPortal(overlay, document.body) : overlay}
    </>
  );
};

export default BillingRequiredOverlay;
